import * as Widgets from '../../widgets';
import { IWidget } from '../../widgets';
import { ILoggerService } from '../general';

export interface IWidgetSerializationService {
    createTypedWidget(w, typeName?: string);
    deserializeWidgets(widgets: IWidget[]): IWidget[];
    parseConfigObject(configuration);
    serializeWidget(w): Widgets.IWidget;
    stripServices(widget: IWidget);
}

export class WidgetSerializationService implements IWidgetSerializationService {
    static serviceId = 'widgetSerializationService';
    static $inject = ['widgetFactory', 'loggerService'];

    constructor(
        private _widgetFactory: Widgets.IWidgetFactory,
        private _loggerService: ILoggerService
    ) {
    }

    serializeWidget(widget: any): Widgets.IWidget {
        const w = this.stripServices(widget);
        if (w.configuration) {
            try {
                if (w.configuration.series && w.configuration.series.length) {
                    w.configuration.series.forEach(s => {
                        if (s.viewModel) {
                            delete s.viewModel;
                        }
                    });
                }
                w.configuration = this.serializeConfigObject(w.configuration);
            } catch (e) {
                w.configuration = null;
            }
        }
        return w;
    }

    createTypedWidget(w, typeName?: string) {
        if (!typeName) {
            typeName = w.type;
        }
        let deserializedConfiguration = undefined;
        if (w.configuration && w.configuration !== '{}') {
            deserializedConfiguration = this.parseConfigObject(w.configuration);
        }
        const widget = this._widgetFactory.createWidget(typeName, deserializedConfiguration);
        if (widget) {
            this.copyFromExisting(w, widget, deserializedConfiguration);
        }
        return widget;
    }

    private copyFromExisting(w: any, widget: Widgets.IWidget, deserializedConfiguration: any) {
        try {
            const originalType = w.type;
            // don't overwrite type to ease backward compatibility
            if (widget.type && w.type) {
                delete w['type'];
            }
            //keep the created configuration in case it is new for the widget, and the saved one does not have one yet
            // Trick for backwards compatibility
            const conf = widget.configuration;
            for (const key of Object.keys(w)) {
                if (w.hasOwnProperty(key)) {
                    widget[key] = w[key];
                }
            }
            widget.configuration = deserializedConfiguration ?? conf;
            this.setNewProperties(widget, conf);
            // Restore type to avoid corrupting the store - yuk
            w.type = originalType;
        } catch (e) {
            this._loggerService.logError(e, false);
        }
    }

    private setNewProperties(widget: Widgets.IWidget, conf: Widgets.IWidgetConfiguration) {
        if (widget.configuration) {
            // set new properties in case some are added with a default value
            try {
                for (const key of Object.keys(conf)) {
                    if (widget.configuration[key] === undefined) {
                        widget.configuration[key] = conf[key];
                    }
                }
            } catch (e) {
                this._loggerService.logError(e, false);
            }
        }
    }

    parseConfigObject(configuration) {
        return (typeof (configuration) === typeof ('')) ?
            JSON.parse(configuration) :
            configuration;
    }

    deserializeWidgets(widgets: IWidget[]): IWidget[] {
        // deserialize widgets
        return widgets.map(w => {
            let parsed = this.createTypedWidget(w);
            if (!parsed) {
                w.configuration = w.configuration
                    ? this.parseConfigObject(w.configuration)
                    : {};
                parsed = w;
            }
            return parsed;
        });
    }

    stripServices(widget: IWidget) {
        // Strip angular services to avoid circular references
        const w: any = {};
        for (const key of Object.keys(widget)) {
            //if starts with _, private property or service=>remove
            if (key.indexOf('$') !== 0 && typeof (widget[key]) !== 'function' && key.indexOf('_') !== 0) {
                w[key] = widget[key];
            }
        }
        return w;
    }

    private serializeConfigObject(configuration) {
        return (typeof (configuration) === typeof ('')) ?
            configuration :
            JSON.stringify(configuration);
    }
}